import { useModal } from 'components/modals/useModal';
import { Input } from 'components/ui/input';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useLazyGetAutomaticClosingPreviewQuery } from 'store/api/tenants';
import { CaseClosingConfigurationDto } from 'types/caseClosing';
import { t } from 'utils/getTranslations';
import { AutomaticClosingConfirmationModal } from './AutomaticClosingConfirmationModal';
import { CaseClosingSubsection, CaseClosingTick } from './CaseClosingSubsection';
import {
    AutomaticClosingSettings,
    automaticClosingConfirmation,
    inactivityDaysErrorMessageId,
    isValidInactivityDays,
    MAX_INACTIVITY_DAYS,
} from './caseClosingConfirmation';
import { useCaseClosingSubsectionEdit } from './useCaseClosingSubsectionEdit';

/** The Practice areas list with its "Excluded from automatic closing" quick filter set to Yes. */
const EXCLUDED_PRACTICE_AREAS_PATH = `/practice-areas?${new URLSearchParams({
    qf: 'isExcludedFromAutomaticClosing=in=(true)',
}).toString()}`;

const DAYS_INPUT_ID = 'automatic-closing-inactivity-days';
const NON_INTEGER_KEYS = new Set(['.', ',', 'e', 'E', '+', '-']);

interface Props {
    caseClosing: CaseClosingConfigurationDto;
}

export function AutomaticClosingSubsection({ caseClosing }: Props) {
    const { isEditMode, isSaving, startEditing, cancel, change, valueOf, save } =
        useCaseClosingSubsectionEdit(caseClosing);
    const [getAutomaticClosingPreview, { isFetching: isLoadingPreview }] = useLazyGetAutomaticClosingPreviewQuery();
    const { openModal, closeModal } = useModal();
    const [days, setDays] = useState<number | ''>(caseClosing.automaticClosingInactivityDays);
    const [showDaysError, setShowDaysError] = useState(false);

    const isEnabled = valueOf('isAutomaticClosingEnabled');
    const isDaysInvalid = showDaysError && isEnabled && !isValidInactivityDays(days);

    function resetDays() {
        setDays(caseClosing.automaticClosingInactivityDays);
        setShowDaysError(false);
    }

    function handleEdit() {
        resetDays();
        startEditing();
    }

    function handleEnabledChange(checked: boolean) {
        change({ isAutomaticClosingEnabled: checked });
        if (!checked) {
            resetDays();
        }
    }

    async function handleSave() {
        let edited: AutomaticClosingSettings = {
            isAutomaticClosingEnabled: false,
            automaticClosingInactivityDays: caseClosing.automaticClosingInactivityDays,
        };
        if (isEnabled) {
            if (!isValidInactivityDays(days)) {
                setShowDaysError(true);
                return;
            }
            edited = { isAutomaticClosingEnabled: true, automaticClosingInactivityDays: days };
        }

        const confirmation = automaticClosingConfirmation(caseClosing, edited);
        if (confirmation === null) {
            await save(edited);
            return;
        }
        try {
            const { caseCount } = await getAutomaticClosingPreview({
                inactivityDays: edited.automaticClosingInactivityDays,
            }).unwrap();
            openModal(
                <AutomaticClosingConfirmationModal
                    confirmation={confirmation}
                    caseCount={caseCount}
                    onCancel={closeModal}
                    onConfirm={() => {
                        closeModal();
                        void save(edited);
                    }}
                />,
                closeModal
            );
        } catch (e) {
            console.error('Automatic closing preview failed', e);
        }
    }

    return (
        <CaseClosingSubsection
            titleId="case-closing-config.automatic-closing"
            isEditMode={isEditMode}
            isSaving={isSaving || isLoadingPreview}
            onEdit={handleEdit}
            onCancel={cancel}
            onSave={() => {
                void handleSave();
            }}
        >
            <CaseClosingTick
                label={t('case-closing-config.close-cases-automatically')}
                checked={isEnabled}
                disabled={!isEditMode}
                onCheckedChange={handleEnabledChange}
            />
            <p className="text-body2 text-fg-secondary">{t('case-closing-config.automatic-closing-intro')}</p>
            <div className="flex flex-col gap-1">
                {isEditMode ? (
                    <>
                        <label htmlFor={DAYS_INPUT_ID} className="text-body2 font-medium">
                            {t('case-closing-config.close-after-no-activity')}
                        </label>
                        <Input
                            id={DAYS_INPUT_ID}
                            type="number"
                            min={1}
                            max={MAX_INACTIVITY_DAYS}
                            step={1}
                            value={days}
                            disabled={!isEnabled}
                            aria-invalid={isDaysInvalid}
                            className="bg-paper h-8 w-28"
                            onChange={(e) => setDays(e.target.value === '' ? '' : Number(e.target.value))}
                            onKeyDown={(e) => {
                                if (NON_INTEGER_KEYS.has(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                        />
                        {isDaysInvalid ? (
                            <p className="text-caption text-error">
                                {t(inactivityDaysErrorMessageId(days), { max: String(MAX_INACTIVITY_DAYS) })}
                            </p>
                        ) : null}
                    </>
                ) : (
                    <>
                        <span className="text-body2 font-medium">
                            {t('case-closing-config.close-after-no-activity')}
                        </span>
                        <p className="text-body1">
                            <FormattedMessage
                                id="case-closing-config.inactivity-days-value"
                                values={{ days: caseClosing.automaticClosingInactivityDays }}
                            />
                        </p>
                    </>
                )}
            </div>
            <p className="text-body2">
                <FormattedMessage
                    id="case-closing-config.excluded-practice-areas"
                    values={{
                        count: caseClosing.excludedPracticeAreaCount,
                        link: (chunks: ReactNode) => (
                            <Link key="link" to={EXCLUDED_PRACTICE_AREAS_PATH} className="text-link hover:underline">
                                {chunks}
                            </Link>
                        ),
                    }}
                />
            </p>
        </CaseClosingSubsection>
    );
}
